import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

type Props = { children: ReactNode };
type State = { error: Error | null };

/**
 * Keeps a route crash from blanking the entire shell (nav/footer stay usable via recovery UI).
 */
export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Route render failed', error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="mx-auto flex min-h-[60vh] max-w-lg flex-col items-center justify-center px-4 py-16 text-center">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-rose-300">
          Page failed to load
        </p>
        <h1 className="mt-3 font-sans text-2xl font-bold text-slate-100">Something went wrong</h1>
        <p className="mt-3 font-sans text-sm text-slate-400">
          {this.state.error.message || 'Unexpected render error.'}
        </p>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <button
            type="button"
            className="btn-primary px-5 py-2.5 text-sm"
            onClick={() => {
              this.setState({ error: null });
              window.location.reload();
            }}
          >
            Reload page
          </button>
          <Link to="/" className="btn-secondary px-5 py-2.5 text-sm">
            Back to home
          </Link>
        </div>
      </div>
    );
  }
}
